import React, { useState, useEffect } from "react";
import Service from "../../Service/firebase";
import "./dashboard.css";
import { subDays, addYears, subYears } from "date-fns";
import { isVendorRole, getVendorId } from "../../utils/rbac";

const DashboardPage = () => {
  const vendorLocked = isVendorRole();
  const lockedVendorId = vendorLocked ? getVendorId() : null;
  const [isLoading, setIsLoading] = useState(false);
  const [getAllDocCount, setGetAllDocCount] = useState({});
  const [totalBookingCount, setTotalBookingCount] = useState(0);
  const [todaysBookingCount, setTodaysBookingCount] = useState(0);
  const [lastWeekBookingCount, setLastWeekBookingCount] = useState(0);

  useEffect(() => {
    let isSubscribed = true;
    if (isSubscribed) {
      getAllDocCountfunc();
      getAllBookingCount();
      getTodayBookingCount();
      getLastWeekBookingCount();
    }
    return () => {
      isSubscribed = false;
    };
  }, []);

  const getAllDocCountfunc = async () => {
    setIsLoading(true);
    if (vendorLocked) {
      // customers use a "vendors" array field, not vendorId; stylists use vendorId
      const [stylistCount, allCustomers] = await Promise.all([
        Service.getCollectionCountByVendor("stylists", lockedVendorId),
        Service.getAllData("customers"),
      ]);
      const customerCount = allCustomers.filter(
        (c) => Array.isArray(c.vendors) && c.vendors.includes(lockedVendorId)
      ).length;
      setGetAllDocCount({ stylists: stylistCount, customers: customerCount });
    } else {
      const allDocCount = await Service.getDocCount();
      setGetAllDocCount(allDocCount);
    }
    setIsLoading(false);
  };

  const getAllBookingCount = async () => {
    setIsLoading(true);
    const today = new Date();
    const startDate = subYears(today, 20);
    const endDate = addYears(today, 20);

    const bookingCount = vendorLocked
      ? await Service.getBookingsCountByVendor(startDate, endDate, "Completed", lockedVendorId)
      : await Service.getBookingsCountWithinDateRangeAndStatus(startDate, endDate, "Completed");
    setTotalBookingCount(bookingCount);
    setIsLoading(false);
  };

  const getTodayBookingCount = async () => {
    setIsLoading(true);
    const today = new Date();

    const bookingCount = vendorLocked
      ? await Service.getBookingsCountByVendor(today, today, "Completed", lockedVendorId)
      : await Service.getBookingsCountWithinDateRangeAndStatus(today, today, "Completed");
    setTodaysBookingCount(bookingCount);
    setIsLoading(false);
  };

  const getLastWeekBookingCount = async () => {
    const today = new Date();
    const lastWeekDate = subDays(today, 7);

    const bookingCount = vendorLocked
      ? await Service.getBookingsCountByVendor(lastWeekDate, today, "Completed", lockedVendorId)
      : await Service.getBookingsCountWithinDateRangeAndStatus(lastWeekDate, today, "Completed");
    setLastWeekBookingCount(bookingCount);
  };

  if (isLoading) {
    return (
      <div className="dashboard_loading">
        <div className="loader">
          <i className="fas fa-spinner fa-pulse"></i>
        </div>
      </div>
    );
  }

  const stats = vendorLocked
    ? [
        {
          title: "Stylists",
          value: getAllDocCount.stylists || 0,
          icon: "fa-scissors",
          color: "#BA79CB",
          badgeColor: "#BA79CB",
        },
        {
          title: "Customers",
          value: getAllDocCount.customers || 0,
          icon: "fa-users",
          color: "#F56954",
          badgeColor: "#F56954",
        },
        {
          title: "Bookings",
          value: totalBookingCount || 0,
          icon: "fa-calendar",
          color: "#667eea",
          badgeColor: "#667eea",
        },
      ]
    : [
        {
          title: "Salon Type",
          value: getAllDocCount.salonType || 0,
          icon: "fa-scissors",
          color: "#BA79CB",
          badgeColor: "#BA79CB",
        },
        {
          title: "User's",
          value: getAllDocCount.users || 0,
          icon: "fa-users",
          color: "#F56954",
          badgeColor: "#F56954",
        },
        {
          title: "Vendor",
          value: getAllDocCount.vendors || 0,
          icon: "fa-briefcase",
          color: "#00B29E",
          badgeColor: "#00B29E",
        },
        {
          title: "Bookings",
          value: totalBookingCount || 0,
          icon: "fa-calendar",
          color: "#667eea",
          badgeColor: "#667eea",
        },
      ];

  const bookingStats = [
    {
      title: "Today's Booking",
      value: todaysBookingCount || 0,
      subLabel: "Scheduled: 0",
      icon: "fa-clock-o",
    },
    {
      title: "Payment Total",
      value: 0,
      subLabel: "Paid: 0",
      icon: "fa-money",
    },
    {
      title: "Last Week's Booking",
      value: lastWeekBookingCount || 0,
      subLabel: "Mon - Wed: 0",
      icon: "fa-calendar",
    },
  ];

  return (
    <div className="dashboard">
      {/* Top Stats Cards */}
      <div className="stats_grid">
        {stats.map((stat, index) => (
          <div key={index} className="stat_card">
            <div className="stat_content">
              <div className="stat_header">
                <h3 className="stat_title">{stat.title}</h3>
                <div className="stat_badge" style={{ backgroundColor: stat.badgeColor }}>
                  <i className={`fa ${stat.icon}`}></i>
                </div>
              </div>
              <div className="stat_details">
                <p className="stat_value" style={{ color: stat.color }}>
                  {stat.value}
                </p>
                {stat.title === "Salon Type" && (
                  <>
                    <span className="stat_detail_item">Male: {getAllDocCount.maleCount || 0}</span>
                    <span className="stat_detail_item">Female: {getAllDocCount.femaleCount || 0}</span>
                    <span className="stat_detail_item">Unisex: {getAllDocCount.unisexCount || 0}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Booking Stats */}
      <div className="booking_stats_grid">
        {bookingStats.map((stat, index) => (
          <div key={index} className="booking_stat_card">
            <div className="booking_stat_header">
              <h3>{stat.title}</h3>
              <i className={`fa ${stat.icon}`}></i>
            </div>
            <p className="booking_stat_value">{stat.value}</p>
            <p className="booking_stat_sub">{stat.subLabel}</p>
          </div>
        ))}
      </div>

      {/* Analytics Section */}
      <div className="analytics_section">
        <h2 className="analytics_title">Analytics</h2>
        <div className="charts_container">
          <div className="chart_placeholder">
            <p>Booking's Growth Chart will appear here</p>
          </div>
          <div className="chart_placeholder">
            <p>Revenue Chart will appear here</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
